import { useState, useRef, useEffect, useCallback } from 'react'
import { Bot, Send, Trash2, Search, Database, BookOpen, BarChart3, ChevronDown, ChevronUp, Loader2, AlertCircle, Headphones, Check } from 'lucide-react'

// ── Types ──────────────────────────────────────────────────────────────────────

type ToolStatus = 'running' | 'done' | 'error'

interface ToolActivity {
  toolName: string
  toolId: string
  status: ToolStatus
  input?: any
  result?: any
  error?: string
}

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  toolActivities?: ToolActivity[]
  isStreaming?: boolean
  error?: string
}

type AiProvider = 'gemini' | 'claude' | 'openai' | 'groq'

const PROVIDER_META: Record<AiProvider, { label: string; desc: string }> = {
  gemini: { label: 'Gemini', desc: 'เร็ว ฟรี เหมาะกับงานทั่วไป' },
  claude: { label: 'Claude', desc: 'แม่นยำสูง ตอบละเอียด' },
  openai: { label: 'ChatGPT', desc: 'สมดุลระหว่างความเร็วและความแม่นยำ' },
  groq:   { label: 'Groq',   desc: 'เร็วมาก ฟรี (GPT-OSS)' },
}

// ── Tool meta ──────────────────────────────────────────────────────────────────

const TOOL_META: Record<string, { label: string; Icon: React.ComponentType<any> }> = {
  search_assets:         { label: 'ค้นหาอุปกรณ์',         Icon: Database  },
  search_tickets:        { label: 'ค้นหา Ticket',         Icon: Headphones },
  search_knowledge_base: { label: 'ค้นหา Knowledge Base', Icon: BookOpen   },
  get_dashboard_summary: { label: 'สรุปภาพรวมระบบ',        Icon: BarChart3  },
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function ToolBadge({ activity }: { activity: ToolActivity }) {
  const [expanded, setExpanded] = useState(false)
  const meta = TOOL_META[activity.toolName] ?? { label: activity.toolName, Icon: Search }
  const { label, Icon } = meta

  const borderColor =
    activity.status === 'error'   ? 'border-red-200   bg-red-50'   :
    activity.status === 'running' ? 'border-blue-200  bg-blue-50'  :
                                    'border-gray-200  bg-gray-50'

  const resultCount =
    activity.result?.count  ??
    activity.result?.assets?.length ??
    activity.result?.tickets?.length ??
    activity.result?.articles?.length

  return (
    <div className={`text-xs rounded-xl overflow-hidden border ${borderColor}`}>
      <button
        onClick={() => activity.status === 'done' && setExpanded(v => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left"
      >
        <Icon size={12} className="text-gray-500 flex-shrink-0"/>
        <span className="font-medium text-gray-700">{label}</span>

        {activity.status === 'running' && (
          <Loader2 size={11} className="ml-auto text-blue-500 animate-spin flex-shrink-0"/>
        )}
        {activity.status === 'done' && (
          <>
            {resultCount !== undefined && (
              <span className="ml-auto text-gray-400">พบ {resultCount} รายการ</span>
            )}
            <span className="text-gray-300 ml-1">
              {expanded ? <ChevronUp size={11}/> : <ChevronDown size={11}/>}
            </span>
          </>
        )}
        {activity.status === 'error' && (
          <AlertCircle size={11} className="ml-auto text-red-500 flex-shrink-0"/>
        )}
      </button>

      {expanded && activity.status === 'done' && activity.result && (
        <div className="px-3 pb-2.5">
          <pre className="text-[10px] leading-relaxed whitespace-pre-wrap overflow-auto max-h-40 bg-white rounded-lg p-2 border border-gray-100 text-gray-600">
            {JSON.stringify(activity.result, null, 2).slice(0, 1200)}
            {JSON.stringify(activity.result).length > 1200 ? '\n…' : ''}
          </pre>
        </div>
      )}
      {activity.status === 'error' && (
        <p className="px-3 pb-2 text-red-600">{activity.error}</p>
      )}
    </div>
  )
}

function AssistantBubble({ msg }: { msg: Message }) {
  // Simple markdown: **bold**, *italic*, `code`, ```blocks```
  function renderText(text: string) {
    const lines = text.split('\n')
    return lines.map((line, li) => {
      const parts = line.split(/(```[\s\S]*?```|`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g)
      return (
        <span key={li}>
          {parts.map((part, pi) => {
            if (part.startsWith('```') && part.endsWith('```')) {
              const code = part.slice(3, -3).replace(/^[a-z]*\n/, '')
              return <pre key={pi} className="bg-gray-100 rounded-lg px-2.5 py-1.5 my-1 text-[11px] overflow-auto font-mono">{code}</pre>
            }
            if (part.startsWith('`') && part.endsWith('`'))
              return <code key={pi} className="bg-gray-100 rounded px-1 font-mono">{part.slice(1, -1)}</code>
            if (part.startsWith('**') && part.endsWith('**'))
              return <strong key={pi}>{part.slice(2, -2)}</strong>
            if (part.startsWith('*') && part.endsWith('*'))
              return <em key={pi}>{part.slice(1, -1)}</em>
            return <span key={pi}>{part}</span>
          })}
          {li < lines.length - 1 && <br/>}
        </span>
      )
    })
  }

  return (
    <div className="flex gap-3 items-start">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
        <Bot size={15} className="text-white"/>
      </div>
      <div className="flex-1 min-w-0 space-y-1.5">
        {/* Tool activities */}
        {(msg.toolActivities ?? []).length > 0 && (
          <div className="space-y-1">
            {msg.toolActivities!.map(a => <ToolBadge key={a.toolId} activity={a}/>)}
          </div>
        )}

        {/* Text content */}
        {(msg.content || msg.isStreaming) && (
          <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 text-sm text-gray-800 shadow-sm leading-relaxed">
            {renderText(msg.content)}
            {msg.isStreaming && (
              <span className="inline-block w-0.5 h-4 bg-gray-400 animate-pulse ml-0.5 rounded align-middle"/>
            )}
          </div>
        )}

        {/* Only streaming spinner when no content yet */}
        {msg.isStreaming && !msg.content && (msg.toolActivities ?? []).length === 0 && (
          <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
            <div className="flex gap-1 items-center">
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}/>
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}/>
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}/>
            </div>
          </div>
        )}

        {/* Error */}
        {msg.error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 text-sm flex items-start gap-2">
            <AlertCircle size={14} className="flex-shrink-0 mt-0.5"/>
            {msg.error}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Suggestions ────────────────────────────────────────────────────────────────

const SUGGESTIONS = [
  { text: 'ภาพรวมระบบ IT เป็นอย่างไร?',     emoji: '📊' },
  { text: 'มี ticket ที่ยังค้างอยู่กี่รายการ?', emoji: '🎫' },
  { text: 'แล็ปท็อปที่ใช้งานอยู่มีกี่เครื่อง?',  emoji: '💻' },
  { text: 'วิธีแก้ไขปัญหา Windows ไม่บูต',    emoji: '🔧' },
]

// ── Chat history persistence ──────────────────────────────────────────────────
// Keyed per user so switching accounts on the same browser doesn't mix histories.

function chatStorageKey(): string | null {
  try {
    const u = localStorage.getItem('user')
    const id = u ? JSON.parse(u).id : null
    return id ? `chatbot_history_${id}` : null
  } catch { return null }
}

function loadStoredMessages(key: string | null): Message[] {
  if (!key) return []
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return []
    const parsed = JSON.parse(raw) as Message[]
    // A message left mid-stream from a previous session is stale — the connection is gone.
    return parsed.map(m => m.isStreaming ? { ...m, isStreaming: false } : m)
  } catch { return [] }
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function ChatbotPage() {
  const storageKey = chatStorageKey()
  const [messages, setMessages] = useState<Message[]>(() => loadStoredMessages(storageKey))
  const [input,    setInput]    = useState('')
  const [loading,  setLoading]  = useState(false)

  // Which AI providers are configured ("พร้อมใช้งาน") for this company, and which
  // one the user currently has selected for this chat session.
  const [readyProviders, setReadyProviders] = useState<AiProvider[]>([])
  const [selectedProvider, setSelectedProvider] = useState<AiProvider | null>(null)
  const [providerModels, setProviderModels] = useState<Partial<Record<AiProvider, string>>>({})
  const [providerMenuOpen, setProviderMenuOpen] = useState(false)

  const bottomRef  = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const abortRef   = useRef<AbortController | null>(null)
  const providerMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Persist chat history so it survives page reloads / navigating away — cleared
  // only when the user explicitly clicks "ล้างแชท".
  useEffect(() => {
    if (!storageKey) return
    try { localStorage.setItem(storageKey, JSON.stringify(messages)) } catch { /* storage full/unavailable — history just won't persist */ }
  }, [messages, storageKey])

  // Close the provider popover on outside click
  useEffect(() => {
    if (!providerMenuOpen) return
    function onClickOutside(e: MouseEvent) {
      if (providerMenuRef.current && !providerMenuRef.current.contains(e.target as Node)) {
        setProviderMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [providerMenuOpen])

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token') ?? ''
        const activeCompanyId = localStorage.getItem('activeCompanyId')
        const headers: Record<string, string> = { Authorization: `Bearer ${token}` }
        if (activeCompanyId) headers['X-Company-Id'] = activeCompanyId
        const res = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/chatbot/providers`, { headers })
        if (!res.ok) return
        const data = await res.json()
        setReadyProviders(data.ready ?? [])
        setSelectedProvider(data.active ?? null)
        setProviderModels(data.models ?? {})
      } catch { /* picker just won't show if this fails */ }
    })()
  }, [])

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 128)}px`
  }, [input])

  const handleEvent = useCallback((event: any, assistantId: string) => {
    setMessages(prev => prev.map(m => {
      if (m.id !== assistantId) return m

      switch (event.type) {
        case 'text':
          return { ...m, content: m.content + event.text }

        case 'tool_start':
          return {
            ...m,
            toolActivities: [...(m.toolActivities ?? []), {
              toolName: event.toolName, toolId: event.toolId,
              status: 'running' as ToolStatus, input: event.input,
            }],
          }

        case 'tool_result':
          return {
            ...m,
            toolActivities: (m.toolActivities ?? []).map(a =>
              a.toolId === event.toolId ? { ...a, status: 'done' as ToolStatus, result: event.result } : a
            ),
          }

        case 'tool_error':
          return {
            ...m,
            toolActivities: (m.toolActivities ?? []).map(a =>
              a.toolId === event.toolId ? { ...a, status: 'error' as ToolStatus, error: event.error } : a
            ),
          }

        case 'error':
          return { ...m, isStreaming: false, error: event.message }

        default:
          return m
      }
    }))
  }, [])

  async function sendMessage(text: string) {
    if (!text.trim() || loading) return

    const userMsg: Message = { id: `u-${Date.now()}`, role: 'user', content: text.trim() }
    const assistantId = `a-${Date.now() + 1}`
    const assistantMsg: Message = {
      id: assistantId, role: 'assistant', content: '',
      isStreaming: true, toolActivities: [],
    }

    setMessages(prev => [...prev, userMsg, assistantMsg])
    setInput('')
    setLoading(true)

    // Build history including the new user message
    const historyForApi = [...messages, userMsg].map(m => ({
      role: m.role as 'user' | 'assistant',
      content: m.content,
    }))

    const abort = new AbortController()
    abortRef.current = abort

    try {
      const token = localStorage.getItem('token') ?? ''
      const activeCompanyId = localStorage.getItem('activeCompanyId')
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      }
      if (activeCompanyId) headers['X-Company-Id'] = activeCompanyId

      const res = await fetch(
        `${import.meta.env.VITE_API_URL || '/api'}/chatbot/chat`,
        {
          method: 'POST', headers,
          body: JSON.stringify({ messages: historyForApi, provider: selectedProvider ?? undefined }),
          signal: abort.signal,
        }
      )

      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: `HTTP ${res.status}` }))
        throw new Error(err.message ?? `HTTP ${res.status}`)
      }

      const reader = res.body!.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try { handleEvent(JSON.parse(line.slice(6)), assistantId) } catch { /* ignore parse error */ }
        }
      }
    } catch (e: any) {
      if (e.name === 'AbortError') return
      setMessages(prev => prev.map(m =>
        m.id === assistantId ? { ...m, isStreaming: false, error: e.message } : m
      ))
    } finally {
      setMessages(prev => prev.map(m =>
        m.id === assistantId ? { ...m, isStreaming: false } : m
      ))
      setLoading(false)
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input) }
  }

  function clearChat() {
    abortRef.current?.abort()
    setMessages([])
    setLoading(false)
    if (storageKey) localStorage.removeItem(storageKey)
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-3.5 flex items-center gap-3 flex-shrink-0">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center shadow-sm">
          <Bot size={18} className="text-white"/>
        </div>
        <div>
          <h1 className="text-sm font-semibold text-gray-900">IT Assistant</h1>
          <p className="text-xs text-gray-400">ผู้ช่วย AI สำหรับระบบ IT Management</p>
        </div>
        {messages.length > 0 && (
          <button
            onClick={clearChat}
            className="ml-auto flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-500 px-2.5 py-1.5 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Trash2 size={13}/> ล้างแชท
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-5 space-y-5">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center pb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center mb-4 shadow-lg">
              <Bot size={28} className="text-white"/>
            </div>
            <h2 className="text-base font-semibold text-gray-800 mb-1">สวัสดี! ฉันคือ IT Assistant</h2>
            <p className="text-sm text-gray-500 mb-6 max-w-xs leading-relaxed">
              ถามฉันเกี่ยวกับอุปกรณ์ IT, tickets, Knowledge Base หรือขอข้อมูลสรุปได้เลย
            </p>
            <div className="grid grid-cols-2 gap-2 w-full max-w-xs">
              {SUGGESTIONS.map(s => (
                <button
                  key={s.text}
                  onClick={() => sendMessage(s.text)}
                  className="flex items-start gap-2 text-xs bg-white border border-gray-200 rounded-xl px-3 py-2.5 text-gray-700 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm transition-all text-left"
                >
                  <span>{s.emoji}</span>
                  <span>{s.text}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map(msg =>
            msg.role === 'user' ? (
              <div key={msg.id} className="flex justify-end">
                <div className="max-w-[75%] bg-blue-600 text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-sm leading-relaxed shadow-sm">
                  {msg.content}
                </div>
              </div>
            ) : (
              <AssistantBubble key={msg.id} msg={msg}/>
            )
          )
        )}
        <div ref={bottomRef}/>
      </div>

      {/* Input */}
      <div className="bg-white border-t border-gray-200 px-4 py-3 flex-shrink-0">
        <div className="max-w-4xl mx-auto border border-gray-300 rounded-2xl focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-shadow">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="พิมพ์ข้อความ… (Enter ส่ง / Shift+Enter ขึ้นบรรทัด)"
            rows={1}
            disabled={loading}
            className="w-full resize-none border-none focus:outline-none focus:ring-0 px-4 pt-3 pb-1 text-sm text-gray-800 placeholder:text-gray-400 min-h-[46px] bg-transparent disabled:bg-transparent"
          />
          <div className="flex items-center justify-end gap-2 px-2.5 pb-2.5">
            {readyProviders.length > 1 && (
              <div className="relative" ref={providerMenuRef}>
                {providerMenuOpen && (
                  <div className="absolute bottom-full right-0 mb-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200 py-1.5 z-10">
                    {readyProviders.map(p => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => { setSelectedProvider(p); setProviderMenuOpen(false) }}
                        className="w-full flex items-start justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800">{PROVIDER_META[p].label}</p>
                          <p className="text-xs text-gray-400">{PROVIDER_META[p].desc}</p>
                          {providerModels[p] && (
                            <p className="text-[10px] text-gray-300 font-mono mt-0.5 truncate">{providerModels[p]}</p>
                          )}
                        </div>
                        {selectedProvider === p && <Check size={14} className="text-blue-600 flex-shrink-0 mt-0.5"/>}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setProviderMenuOpen(v => !v)}
                  title={selectedProvider ? providerModels[selectedProvider] : undefined}
                  className="flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full pl-3 pr-2 py-1.5 transition-colors max-w-[200px]"
                >
                  <span className="truncate">
                    {selectedProvider ? PROVIDER_META[selectedProvider].label : '…'}
                    {selectedProvider && providerModels[selectedProvider] && (
                      <span className="text-gray-400 font-normal"> · {providerModels[selectedProvider]}</span>
                    )}
                  </span>
                  <ChevronDown size={12} className="flex-shrink-0"/>
                </button>
              </div>
            )}
            <button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || loading}
              className="w-9 h-9 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 text-white disabled:text-gray-400 flex items-center justify-center transition-colors flex-shrink-0 shadow-sm"
            >
              {loading
                ? <Loader2 size={16} className="animate-spin"/>
                : <Send size={16}/>
              }
            </button>
          </div>
        </div>
        <p className="text-center text-[10px] text-gray-400 mt-1.5">
          IT Assistant อาจมีข้อผิดพลาดได้ โปรดตรวจสอบข้อมูลสำคัญเพิ่มเติม
        </p>
      </div>
    </div>
  )
}
